import type {
  QuickChatResponse,
  DeepResearchResponse,
  Report,
  ShareReportResponse,
  SharedReportResponse,
} from '../types'

// ================================
// Quick Chat Mock Data
// ================================

export const mockQuickChatResponse: QuickChatResponse = {
  content: `**BTC (Bitcoin) 当前分析：**

**价格概况**：
- 当前价格：$67,234 USD
- 24小时涨跌：+2.34%
- 市值：$1.31T (加密货币排名 #1)

**关键观点**：
1. **技术面**：BTC突破65,000美元阻力位，RSI 62（中性偏多）
2. **链上数据**：鲸鱼地址增持，交易所BTC流出量增加（看涨信号）
3. **宏观环境**：美联储降息预期升温，机构持仓稳定

**短期预测**：
- 支撑位：$64,000
- 阻力位：$70,000
- 建议：适合中长期持有，短期可能在65k-70k区间震荡

💡 提示：如需深度分析报告（包含图表、竞品对比、风险评估），请切换到 **Deep Research** 模式。`,
  symbol: 'BTC',
  query_type: 'price',
  response_time: 1.8,
  model: 'anthropic/claude-3.5-sonnet',
  session_id: 'mock-conv-001',
}

// ================================
// Deep Research Mock Data
// ================================

export const mockDeepResearchMarkdown = `# Bitcoin (BTC) 深度研究报告

**生成时间**: 2025-10-26 15:30:00 UTC
**数据来源**: CoinGecko, Etherscan, Twitter, Reddit, CryptoPanic
**报告类型**: Deep Research

---

## 🎯 TL;DR（一句话总结）

比特币突破关键阻力位$65,000，链上活跃度上升，机构持仓稳定，短期有望测试$70,000，中长期看涨趋势不变。

### 看涨理由（Bull Case）

- ✅ 突破$65,000重要阻力位，技术面转强
- ✅ 鲸鱼地址持续增持，交易所BTC流出量增加
- ✅ 美联储降息预期升温，宏观流动性改善
- ✅ 机构持仓稳定（贝莱德IBIT持仓+5.2%）
- ✅ 链上活跃地址数+12%，网络健康度良好

### 看跌理由（Bear Case）

- ⚠️ $70,000压力位较强，历史上多次受阻
- ⚠️ USDT溢价率下降，稳定币增量资金不足
- ⚠️ 美国大选不确定性，10月政策风险升高
- ⚠️ 矿工抛压增加，算力难度调整后收益下降

### 关键催化剂

1. **美联储11月议息会议** (2025-11-07) - 降息50bp预期
2. **比特币ETF期权获批** (预计Q4) - 机构配置需求增加
3. **减半后供给减半效应** - 历史上6-12个月后启动牛市

### 风险等级
**中等风险** - 短期波动性高，中长期基本面支持

### 建议投资期限
**6-18个月** - 适合中长期持有，短期可能震荡

---

## ⏰ 时间窗分析

### 短期窗口（7天）

**预测价格区间**: $64,000 - $72,000
**关键事件**:
- 10月27日：美国CPI数据公布
- 10月30日：美股财报季（苹果/微软/谷歌）

**技术指标**:
| 指标 | 数值 | 信号 |
|------|------|------|
| RSI (14日) | 62.4 | 中性偏多 |
| MACD | 金叉 | 看涨 |
| 布林带 | 中轨附近 | 震荡 |

**建议操作**: 观望为主，$65,000以下可逢低买入

### 中期窗口（30天）

**预测价格区间**: $60,000 - $80,000
**关键事件**:
- 11月7日：美联储议息会议
- 11月15日：比特币期权到期（$23亿名义价值）

**链上指标**:
| 指标 | 当前值 | 7日变化 | 趋势 |
|------|--------|---------|------|
| 活跃地址数 | 1.02M | +12.3% | ↑ |
| 交易量 | $28.4B | -3.2% | → |
| 交易所余额 | 2.31M BTC | -0.8% | ↓ (看涨) |

**建议操作**: 分批建仓，目标价$75,000

### 长期窗口（6个月）

**预测价格区间**: $50,000 - $100,000
**宏观逻辑**:
- 美联储降息周期开启（2025-2026年）
- 比特币ETF资金持续流入（年化+$120亿）
- 减半后供给减半效应（历史规律6-12个月后显现）

**建议操作**: 核心仓位持有，目标$80,000-$100,000

---

## 📊 社交媒体情绪分析

### 情绪分布

| 来源 | 提及次数 (24h) | 看涨占比 | 看跌占比 | 中性占比 |
|------|----------------|----------|----------|----------|
| Twitter | 45,231 | 58% | 22% | 20% |
| Reddit (r/Bitcoin) | 12,456 | 63% | 15% | 22% |
| Telegram | 8,923 | 55% | 25% | 20% |
| **加权平均** | **66,610** | **59%** | **21%** | **20%** |

### 关键意见领袖观点

**@PlanB (Stock-to-Flow模型创建者)**:
> "BTC突破$65k是预期内的，S2F模型目标$100k未变。11月后加速上涨可能性75%。"

**@Willy Woo (链上分析师)**:
> "交易所BTC流出速度加快，供给紧缩信号明确。持币者平均成本$58k，当前价格仍有20%利润空间。"

**@CryptoQuant CEO**:
> "矿工储备比率下降至0.82（历史低位），抛压减弱。机构持仓占比达18.7%（历史新高）。"

### 情绪趋势
📈 **看涨情绪上升** - 过去7天情绪指数从52上升至59（满分100）

---

## 📉 技术面分析

### 价格走势图

![BTC Price Chart](data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAA4QAAAHCCAYAAACqH0NHAAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAAAJcEhZcwAAEnQAABJ0Ad5mH3gAADuOSURBVHhe7d0HnBXF/cDx31

### 关键支撑/阻力位

| 级别 | 价格 | 类型 | 说明 |
|------|------|------|------|
| 强阻力 | $72,000 | 历史高点 | 2024年3月历史最高价，心理压力大 |
| 次阻力 | $70,000 | 整数关口 | 2025年多次受阻，需放量突破 |
| 当前价格 | $67,234 | - | - |
| 次支撑 | $64,000 | 前高 | 9月突破后的回踩支撑 |
| 强支撑 | $60,000 | MA200 | 200日均线支撑，跌破转空 |

### 技术指标详解

**1. 移动平均线（MA）**:
- MA20: $65,400 (黄金交叉)
- MA50: $63,200
- MA200: $60,100
- **趋势**: 多头排列，中长期看涨

**2. 相对强弱指标（RSI）**:
- RSI(14): 62.4
- 超买线(70)距离: 7.6点
- **解读**: 多头动能充足，未进入超买区

**3. MACD**:
- DIF: 1,234
- DEA: 892
- 柱状图: +342 (放大中)
- **信号**: 金叉，看涨

**4. 布林带（Bollinger Bands）**:
- 上轨: $69,500
- 中轨: $66,000
- 下轨: $62,500
- **位置**: 中轨上方，震荡偏多

---

## ⛓️ 链上数据分析

### 持币地址分布

| 持币量级别 | 地址数 | 占比 | 7日变化 |
|-----------|--------|------|---------|
| > 10,000 BTC (鲸鱼) | 102 | 0.0003% | +3 (+3.0%) |
| 1,000-10,000 BTC | 2,145 | 0.005% | +12 (+0.6%) |
| 100-1,000 BTC | 16,234 | 0.038% | -23 (-0.1%) |
| 10-100 BTC | 156,789 | 0.37% | +456 (+0.3%) |
| 1-10 BTC | 1,234,567 | 2.9% | +2,341 (+0.2%) |
| < 1 BTC | 41,234,567 | 96.7% | +12,345 (+0.03%) |

**解读**: 鲸鱼地址增加3个，增持信号；中等地址(100-1000 BTC)小幅减少，获利了结。

### 交易所流入流出

| 指标 | 数值 | 7日变化 | 趋势 |
|------|------|---------|------|
| 交易所BTC余额 | 2,310,456 BTC | -18,234 BTC (-0.8%) | ↓ (看涨) |
| 交易所流入量 (24h) | 12,345 BTC | -2.3% | ↓ |
| 交易所流出量 (24h) | 14,567 BTC | +3.4% | ↑ (看涨) |
| 净流出量 (24h) | 2,222 BTC | - | ↑ (看涨) |

**解读**: 交易所BTC持续流出，供给紧缩，看涨信号。

### 矿工行为

| 指标 | 数值 | 说明 |
|------|------|------|
| 矿工储备量 | 1,823,456 BTC | 历史中位数 |
| 矿工储备比率 | 0.82 | 低于1.0表示抛压减弱 |
| 算力 | 523 EH/s | 历史最高 |
| 挖矿难度 | 68.2T | 下次调整预计 -1.2% |
| 挖矿成本 | $54,000/BTC | 当前价高于成本24% |

**解读**: 矿工储备比率下降，抛压减弱；算力新高，网络安全性提升。

---

## 🏆 竞品对比分析

### 市值Top 10对比表

| 项目 | 市值 | 24h涨跌 | 7d涨跌 | FDV/TVL | P/S倍数 | 评级 |
|------|------|---------|--------|---------|---------|------|
| **BTC** | **$1.31T** | **+2.34%** | **+8.1%** | **N/A** | **N/A** | **A+** |
| ETH | $395B | +1.87% | +6.3% | 2.8x | 12.4x | A |
| BNB | $91B | +0.54% | +3.2% | 4.1x | 18.7x | B+ |
| SOL | $78B | +5.23% | +12.7% | 1.9x | 24.3x | A- |
| XRP | $32B | -0.34% | +2.1% | N/A | N/A | C+ |

### 估值对比

| 项目 | FDV/Revenue | 溢价/折扣 vs BTC | 估值合理性 |
|------|-------------|------------------|-----------|
| BTC | 基准 | 0% | 公允 |
| ETH | 1.2x | +20% | 略贵 |
| SOL | 2.1x | +110% | 高估 |
| BNB | 1.5x | +50% | 中等 |

**结论**: BTC作为加密货币龙头，估值相对合理，ETH和SOL估值偏高。

---

## 💰 代币经济学分析

### 供应结构

| 指标 | 数值 | 说明 |
|------|------|------|
| 最大供应量 | 21,000,000 BTC | 固定上限 |
| 当前流通量 | 19,543,210 BTC | 占比93.1% |
| 剩余待挖量 | 1,456,790 BTC | 约需120年挖完 |
| 减半周期 | 4年 | 下次减半：2028年 |
| 当前区块奖励 | 3.125 BTC | 2024年4月减半后 |

### 解锁时间表

| 日期 | 事件 | 数量 | 流通占比 | 影响 |
|------|------|------|----------|------|
| 2025-10-28 | 每日挖矿产出 | ~450 BTC | +0.002% | 极小 |
| 2028-04 | 下次减半 | 奖励减至1.5625 BTC/block | - | 供给减半 |
| 2140年 | 挖矿结束 | 0 BTC | 100% | 纯手续费时代 |

**通胀率**: 当前年化通胀率 ~1.7%（逐年递减）

### 价值捕获机制

1. **稀缺性**: 固定2100万枚上限，抗通胀
2. **网络效应**: 市值最大，流动性最强
3. **安全性**: 算力最高，攻击成本极高
4. **去中心化**: 无单一控制方，抗审查

---

## ⚠️ 风险评估

### 关键风险矩阵

| 风险类别 | 风险程度 | 发生概率 | 影响程度 | 缓解措施 |
|---------|---------|---------|---------|---------|
| 监管风险 | 高 | 60% | 严重 | 持续关注SEC政策 |
| 技术风险 | 低 | 10% | 中等 | 定期升级节点软件 |
| 市场风险 | 中 | 40% | 中等 | 设置止损位 |
| 宏观风险 | 中 | 50% | 严重 | 分散配置 |
| 黑天鹅风险 | 低 | 5% | 严重 | 仓位控制 |

### 详细风险说明

**1. 监管风险（高）**
- **描述**: SEC对加密货币监管收紧，可能限制交易或ETF
- **概率**: 60% - 2025年大选后政策不确定性高
- **影响**: 短期可能跌破$50,000
- **应对**: 密切关注SEC公告，预留30%现金应对抛售

**2. 技术风险（低）**
- **描述**: 比特币网络遭受51%攻击或重大漏洞
- **概率**: 10% - 算力全球最高，攻击成本极大
- **影响**: 中等 - 信心受损但网络可恢复
- **应对**: 分散存储，使用硬件钱包

**3. 市场风险（中）**
- **描述**: 大户抛售或市场恐慌引发踩踏
- **概率**: 40% - 历史上每轮牛市都有30-50%回调
- **影响**: 中等 - 可能回调至$55,000支撑
- **应对**: 设置止损位$60,000，分批建仓

**4. 宏观风险（中）**
- **描述**: 美国经济衰退或美元大幅升值
- **概率**: 50% - 2025年经济软着陆概率50/50
- **影响**: 严重 - 避险情绪升温，资金撤离风险资产
- **应对**: 配置黄金/国债对冲，降低杠杆

### 催化剂日历

| 日期 | 事件 | 重要性 | 预期影响 |
|------|------|--------|---------|
| 2025-10-27 | 美国CPI数据 | ⭐⭐⭐ | 通胀下降→看涨 |
| 2025-11-07 | 美联储议息会议 | ⭐⭐⭐⭐⭐ | 降息50bp→大涨 |
| 2025-11-15 | BTC期权到期 | ⭐⭐⭐ | 波动加剧 |
| 2025-12-01 | 美国大选投票日 | ⭐⭐⭐⭐ | 政策不确定性 |

### 情景分析

**牛市情景（概率40%）**:
- 触发条件：美联储降息50bp + BTC ETF期权获批
- 价格目标：$100,000 - $120,000
- 时间窗口：2026年Q1-Q2

**震荡情景（概率50%）**:
- 触发条件：降息25bp + 监管政策维持现状
- 价格目标：$60,000 - $80,000
- 时间窗口：2025年Q4 - 2026年Q1

**熊市情景（概率10%）**:
- 触发条件：SEC禁止加密货币交易 + 经济衰退
- 价格目标：$40,000 - $50,000
- 时间窗口：2025年Q4

---

## 🎯 投资结论与建议

### 核心观点

1. **短期（7天）**: 震荡偏多，区间$64k-$72k
2. **中期（30天）**: 看涨，目标$75k
3. **长期（6个月）**: 强烈看涨，目标$80k-$100k

### 投资建议

| 风险偏好 | 建议仓位 | 入场价位 | 止损价位 | 目标价位 | 持仓周期 |
|---------|---------|---------|---------|---------|---------|
| 激进型 | 60-80% | 市价 | $60,000 | $80,000 | 3-6个月 |
| 稳健型 | 30-50% | $64,000以下 | $58,000 | $75,000 | 6-12个月 |
| 保守型 | 10-20% | $60,000以下 | $55,000 | $70,000 | 12个月+ |

### 操作策略

**买入策略**:
- ✅ 分批建仓：$65,000以下每跌2%买入20%仓位
- ✅ 定投策略：每周定投总资金的5%
- ✅ 突破买入：放量突破$70,000后追涨

**卖出策略**:
- 🔴 止损：跌破$60,000立即止损
- 🟡 止盈：$80,000卖出50%，$100,000全部获利了结
- 🟢 滚动操作：$70,000卖出30%，回调$65,000后再买回

### 风险提示

⚠️ **本报告仅供参考，不构成投资建议。加密货币投资风险极高，可能损失全部本金。**

---

## 📜 免责声明

本报告由AI自动生成，数据来源于公开API（CoinGecko、Etherscan、Twitter、Reddit、CryptoPanic）。报告内容仅供参考，不构成任何投资建议。

- 价格预测基于历史数据和技术分析，不保证未来准确性
- 链上数据可能存在延迟或误差
- 社交媒体情绪分析可能受机器人账号影响
- 监管政策、黑天鹅事件等不可预测因素可能导致实际走势与预测偏离

**投资有风险，入市需谨慎。请根据自身风险承受能力做出决策。**

---

## 📊 报告元数据

| 项目 | 详情 |
|------|------|
| 生成时间 | 2025-10-26 15:30:00 UTC |
| 报告版本 | v1.0 |
| 数据时效 | 截至 2025-10-26 14:00 UTC |
| AI模型 | Claude 3.5 Sonnet (via OpenRouter) |
| 数据源数量 | 5个 (CoinGecko, Etherscan, Twitter, Reddit, CryptoPanic) |
| 报告质量得分 | 87/100 (高质量) |
| 生成耗时 | 28.4秒 |

**数据源列表**:
1. CoinGecko - 价格、市值、交易量
2. Etherscan - 链上数据、交易统计
3. Twitter API - 社交情绪、KOL观点
4. Reddit API - 社区讨论、情绪指数
5. CryptoPanic - 新闻聚合、事件日历

---

**报告结束** | 如需更新数据，请重新生成报告
`

export const mockDeepResearchResponse: DeepResearchResponse = {
  report_id: 1001,
  markdown_content: mockDeepResearchMarkdown,
  tldr: '比特币突破关键阻力位$65,000，链上活跃度上升，机构持仓稳定，短期有望测试$70,000，中长期看涨趋势不变。',
  quality_score: 87,
  generation_time: 28.4,
  data_sources: ['CoinGecko', 'Etherscan', 'Twitter', 'Reddit', 'CryptoPanic'],
}

// ================================
// Report Mock Data
// ================================

export const mockReport: Report = {
  id: 1001,
  symbol: 'BTC',
  query: '分析BTC',
  title: 'Bitcoin (BTC) 深度研究报告',
  markdown_content: mockDeepResearchMarkdown,
  tldr: '比特币突破关键阻力位$65,000，链上活跃度上升，机构持仓稳定，短期有望测试$70,000，中长期看涨趋势不变。',
  report_type: 'deep_research',
  status: 'completed',
  quality_score: 87,
  generation_time: 28.4,
  data_sources: ['CoinGecko', 'Etherscan', 'Twitter', 'Reddit', 'CryptoPanic'],
  created_at: '2025-10-26T15:30:00Z',
  completed_at: '2025-10-26T15:30:28Z',
}

// ================================
// Share Mock Data
// ================================

export const mockShareReportResponse: ShareReportResponse = {
  share_token: 'mock-token-abc123xyz',
  share_url: 'http://localhost:3000/shared/mock-token-abc123xyz',
  expires_at: '2025-11-26T15:30:00Z',
}

export const mockSharedReportResponse: SharedReportResponse = {
  title: 'Bitcoin (BTC) 深度研究报告',
  symbol: 'BTC',
  markdown_content: mockDeepResearchMarkdown,
  tldr: '比特币突破关键阻力位$65,000，链上活跃度上升，机构持仓稳定，短期有望测试$70,000，中长期看涨趋势不变。',
  report_type: 'deep_research',
  quality_score: 87,
  data_sources: ['CoinGecko', 'Etherscan', 'Twitter', 'Reddit', 'CryptoPanic'],
  created_at: '2025-10-26T15:30:00Z',
}

// ================================
// SSE Stream Mock Data
// ================================

/**
 * 模拟SSE流式数据
 * 将完整报告分成30段，每段间隔2秒推送
 */
export const mockSSEChunks: string[] = [
  '# Bitcoin (BTC) 深度研究报告\n\n',
  '**生成时间**: 2025-10-26 15:30:00 UTC  \n',
  '**数据来源**: CoinGecko, Etherscan, Twitter, Reddit, CryptoPanic  \n',
  '**报告类型**: Deep Research\n\n---\n\n',
  '## 🎯 TL;DR（一句话总结）\n\n',
  '比特币突破关键阻力位$65,000，链上活跃度上升，机构持仓稳定，',
  '短期有望测试$70,000，中长期看涨趋势不变。\n\n',
  '### 看涨理由（Bull Case）\n\n',
  '- ✅ 突破$65,000重要阻力位，技术面转强\n',
  '- ✅ 鲸鱼地址持续增持，交易所BTC流出量增加\n',
  '- ✅ 美联储降息预期升温，宏观流动性改善\n',
  '- ✅ 机构持仓稳定（贝莱德IBIT持仓+5.2%）\n',
  '- ✅ 链上活跃地址数+12%，网络健康度良好\n\n',
  '### 看跌理由（Bear Case）\n\n',
  '- ⚠️ $70,000压力位较强，历史上多次受阻\n',
  '- ⚠️ USDT溢价率下降，稳定币增量资金不足\n',
  '- ⚠️ 美国大选不确定性，10月政策风险升高\n',
  '- ⚠️ 矿工抛压增加，算力难度调整后收益下降\n\n',
  '### 关键催化剂\n\n',
  '1. **美联储11月议息会议** (2025-11-07) - 降息50bp预期\n',
  '2. **比特币ETF期权获批** (预计Q4) - 机构配置需求增加\n',
  '3. **减半后供给减半效应** - 历史上6-12个月后启动牛市\n\n',
  '### 风险等级\n',
  '**中等风险** - 短期波动性高，中长期基本面支持\n\n',
  '### 建议投资期限\n',
  '**6-18个月** - 适合中长期持有，短期可能震荡\n\n',
  '---\n\n## ⏰ 时间窗分析\n\n### 短期窗口（7天）\n\n',
  '**预测价格区间**: $64,000 - $72,000\n\n...\n\n',
  '*(报告内容生成完毕)*\n\n',
  '---\n\n**报告结束** | 如需更新数据，请重新生成报告',
]

/**
 * 生成SSE事件数据
 */
export const generateSSEEvents = (): Array<{ data: string; delay: number }> => {
  return mockSSEChunks.map((chunk, index) => ({
    data: JSON.stringify({
      content: chunk,
      stage: index < 5 ? 'tldr' : index < 10 ? 'timeframe' : index < 15 ? 'sentiment' : 'conclusion',
      progress: Math.floor((index / mockSSEChunks.length) * 100),
    }),
    delay: 2000, // 每段间隔2秒
  }))
}
